import { useTransition } from "react";
import { Star, ThumbsUp, ThumbsDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { DECISIONS, type Decision } from "@/lib/constants";
import { useDict } from "@/lib/i18n/context";

const tone: Record<Decision, "blue" | "green" | "red"> = {
  shortlisted: "blue",
  accepted: "green",
  rejected: "red",
};

export function DecisionBadge({ decision }: { decision: string | null }) {
  const t = useDict();
  if (!decision) return null;
  const d = decision as Decision;
  return <Badge tone={tone[d] ?? "neutral"}>{t.decision[decision] ?? decision}</Badge>;
}

const icons: Record<Decision, typeof Star> = {
  shortlisted: Star,
  accepted: ThumbsUp,
  rejected: ThumbsDown,
};

export function DecisionControl({
  interviewId,
  current,
}: {
  interviewId: string;
  current: string | null;
}) {
  const t = useDict();
  const [pending, startTransition] = useTransition();

  function choose(d: Decision) {
    const next = current === d ? null : d;
    startTransition(async () => {
      await fetch(`/api/interviews/${interviewId}/decision`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ decision: next }),
      });
      window.location.reload();
    });
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {DECISIONS.map((d) => {
        const Icon = icons[d];
        const active = current === d;
        const activeCls =
          d === "accepted"
            ? "bg-emerald-600 text-white border-emerald-600"
            : d === "rejected"
              ? "bg-red-600 text-white border-red-600"
              : "bg-brand-600 text-white border-brand-600";
        return (
          <button
            key={d}
            type="button"
            disabled={pending}
            onClick={() => choose(d)}
            className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-60 ${
              active ? activeCls : "border-border bg-card text-foreground/80 hover:bg-surface"
            }`}
          >
            <Icon className="h-4 w-4" />
            {t.decision[d]}
          </button>
        );
      })}
    </div>
  );
}
